// v2 sawmill — a long open cutting shed under a gabled plank roof: logs roll off
// a west-side ramp onto a long saw table that feeds a tall vertical frame saw
// mid-shed, and the cut boards stack on the east side over a pale sawdust heap.
// A working timber yard. Ported 1:1 from age-of-mcp's v2/sawmill.ts, using the
// library's shared `foundationSlab`, `scaffold` and `industry` (gableRoof)
// helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, EFFECT_LOOP, type Part } from "../../lib";

const TIMBER  = "#6b4523";   // shed framing
const WOOD    = "#8a5a2c";   // saw table
const PLANK   = "#b08948";   // fresh-cut boards
const LOG     = "#7a5526";   // round logs
const BARK    = "#5e4023";   // log ends / ramp rails
const ROOF    = "#5b3d22";   // plank roof
const METAL   = "#b8bdc4";   // the saw blade
const DUST    = "#d8c79a";   // sawdust

const BASE = 0.14;           // foundation slab top
const SAW_X = 0.08;          // the frame saw sits here along the table

// The long saw table running the length of the shed.
function sawTable(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.84, 0.07, 0.26), color: WOOD, position: [0, BASE + 0.21, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.18, 0.22), color: BARK, position: [-0.32, BASE + 0.09, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.18, 0.22), color: BARK, position: [0.32, BASE + 0.09, 0] },
  ];
}

// The tall vertical frame saw straddling the table: two uprights, a top and
// bottom crossbeam, and the upright metal blade between them.
function frameSaw(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.05, 0.42, 0.05), color: TIMBER, position: [SAW_X, BASE + 0.42, -0.14] },
    { geometry: new THREE.BoxGeometry(0.05, 0.42, 0.05), color: TIMBER, position: [SAW_X, BASE + 0.42, 0.14] },
    { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.33), color: TIMBER, position: [SAW_X, BASE + 0.6, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.33), color: TIMBER, position: [SAW_X, BASE + 0.26, 0] },
    // The blade is named so the "active" animation can stroke it up and down.
    { geometry: new THREE.BoxGeometry(0.015, 0.3, 0.07), color: METAL, position: [SAW_X, BASE + 0.42, 0], name: "blade" },
  ];
}

// The log being fed through the saw, lying along the table.
function feedLog(): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.065, 0.065, 0.56, 7),
    color: LOG, position: [-0.16, BASE + 0.31, 0], rotation: [0, 0, Math.PI / 2],
  };
}

// The west-side log ramp: two sloped rails with two waiting logs resting on
// them, rolled up toward the table.
function logRamp(): Part[] {
  const tilt = 0.42;
  const parts: Part[] = [];
  for (const z of [-0.1, 0.1]) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.4, 0.04, 0.05), color: BARK,
      position: [-0.42, BASE + 0.12, z], rotation: [0, 0, -tilt],
    });
  }
  parts.push(
    { geometry: new THREE.CylinderGeometry(0.06, 0.06, 0.34, 7), color: LOG,
      position: [-0.5, BASE + 0.09, 0], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.06, 0.06, 0.34, 7), color: BARK,
      position: [-0.36, BASE + 0.17, 0], rotation: [Math.PI / 2, 0, 0] },
  );
  return parts;
}

// Cut boards stacked on the east side, plus the sawdust heap under the saw.
function yard(): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < 3; i++) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.34, 0.045, 0.16), color: PLANK,
      position: [0.32, BASE + 0.025 + i * 0.05, 0.3], rotation: [0, 0.12 - i * 0.12, 0],
    });
  }
  parts.push({ geometry: new THREE.ConeGeometry(0.1, 0.1, 6), color: DUST,
    position: [SAW_X, BASE + 0.05, 0.2] });
  return parts;
}

// The open shed: six posts carrying a long gabled plank roof.
function shed(): Part[] {
  const parts: Part[] = [];
  for (const x of [-0.36, 0, 0.36]) {
    for (const z of [-0.28, 0.28]) {
      parts.push({
        geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.44, 6),
        color: TIMBER, position: [x, BASE + 0.22, z],
      });
    }
  }
  parts.push(...industry.gableRoof(0, 0, BASE + 0.44, 0.98, 0.78, 0.2, ROOF, BARK));
  return parts;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), ...sawTable(), ...logRamp(), ...scaffold(0.84, 0.5)];
  if (s === 2) return [foundationSlab(0.86), ...sawTable(), ...logRamp(), ...frameSaw(), ...scaffold(0.84, 0.85)];
  return [
    foundationSlab(0.86),
    ...sawTable(), ...logRamp(), ...frameSaw(), feedLog(), ...yard(), ...shed(),
  ];
}

export const sawmillV2 = defineModel({
  id: "sawmill-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Sawmill (v2)",
  tags: ["building","sawmill","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly sawmill.",
  description: "A timber saw bench that saws raw wood into planks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The vertical frame saw strokes up and down between its crossbeams,
      // four strokes per loop.
      id: "active",
      label: "Saw stroking",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        {
          target: "blade",
          pivot: [SAW_X, BASE + 0.42, 0],
          position: [
            { t: 0, value: [0, 0.05, 0] },
            { t: EFFECT_LOOP * 0.125, value: [0, -0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.25, value: [0, 0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.375, value: [0, -0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.5, value: [0, 0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.625, value: [0, -0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.75, value: [0, 0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.875, value: [0, -0.05, 0], ease: "sine" },
            { t: EFFECT_LOOP, value: [0, 0.05, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "bench-a", position: [0.08, 0.14, 0.42], facing: Math.PI },
    { id: "bench-b", position: [0.08, 0.14, -0.42], facing: 0 },
  ],
  interactions: [
    { id: "saw", label: "Sawing", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
